/**
 * 自動入札ルールの状態を 1 箇所に集約するフック。
 *
 * spec `approval-flow.md` の AC-APF-035〜039 は自動入札ルールの UI を
 * **2 つの場所**に置く:
 *
 * - **AC-APF-037**: 承認マトリクスの各「自動」セル内に 2 行サマリ + 「ルール編集」
 * - **AC-APF-035/036**: マトリクス直下のパネルに説明 + 「全エリア一括編集」
 *
 * どちらも同じルールデータ・同じ bid-limits 実効値・同じ編集モーダルを使うので、
 * データ取得と編集中エリアの state をここへ寄せ、セル用の描画関数とモーダルを
 * 返す。マトリクス側 (ApprovalMatrix → MatrixRow → MatrixModeCell) へは
 * `renderCellSummary` を 1 本だけ渡す (ルール・bid-limits・保存ハンドラを
 * マトリクス 3 階層に prop drilling しないため)。
 */
import { useCallback, useMemo, useState } from 'react'

import { useQuery } from '@connectrpc/connect-query'
import {
  ApprovalMode,
  BusinessProcess,
} from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { type AutoBidRule } from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import { getBidLimitMatrix } from '@ebalance/proto/bffuser/v1/bid_limit_service-BidLimitService_connectquery'
import { useTranslation } from 'react-i18next'

import { toaster } from '@enechain/ecloud-designsystem'

import { AutoBidBulkSummary } from '~/pages/AdminApprovalFlow/components/AutoBidBulkSummary'
import { AutoBidCellSummary } from '~/pages/AdminApprovalFlow/components/AutoBidCellSummary'
import { AutoBidEditModal } from '~/pages/AdminApprovalFlow/components/AutoBidEditModal'
import { AutoBidIntradayEditModal } from '~/pages/AdminApprovalFlow/components/AutoBidIntradayEditModal'
import {
  AREA_CODE_LABELS,
  type ProcessDef,
  makeNoSectionCellKey,
} from '~/pages/AdminApprovalFlow/constants/processMap'
import { useAutoBidFee } from '~/pages/AdminApprovalFlow/hooks/useAutoBidFee'
import { useSaveAutoBidRule } from '~/pages/AdminApprovalFlow/hooks/useSaveAutoBidRule'
import {
  type AutoBidSideLocal,
  autoBidIntradaySideFromRule,
  autoBidIntradaySideSummaryText,
  autoBidIntradaySideToProto,
  autoBidSideFromRule,
  autoBidSpotSideToProto,
  computeAutoBidEffective,
  computeAutoBidIntradayEffective,
  groupAutoBidRulesByArea,
  isAutoBidIntradayRuleSaved,
  isAutoBidRuleUnsaved,
} from '~/pages/AdminApprovalFlow/models/autoBid'
import {
  autoBidMarketOrderAllowed,
  autoBidPriceRangeFromCells,
  autoBidQuantityLimitFromCells,
} from '~/pages/AdminApprovalFlow/models/autoBidBidLimits'
import type { ProcessState } from '~/pages/AdminApprovalFlow/models/reducer'

export type AutoBidSection = {
  /** 承認モード = 自動 のエリア (AC-APF-036/037 の分岐条件)。 */
  autoAreas: AreaCode[]
  /**
   * 「自動」セルの中身 (AC-APF-037)。自動でないエリアは null を返すので、
   * マトリクス側は返り値をそのまま描けばよい。
   */
  renderCellSummary: (area: AreaCode) => React.ReactElement | null
  /**
   * 一括セレクト = `自動` (マトリクス非表示) の時に出す、 保存済みルールの要約
   * (AC-APF-060)。 自動エリアが 0 件なら null。
   */
  bulkSummary: React.ReactElement | null
  /**
   * 保存済みの自動入札ルールが 1 つでもあるか。
   *
   * 一括編集ボタンの文言を切り替えるために使う (デザイナー指摘 2026-09-14
   * 「一度保存状態では『全エリア一括編集』→『編集』のみ」)。 保存後は隣に
   * 「現在設定中のルール」 の箱が出ており、 何を編集するかは文脈で分かるため。
   *
   * `bulkSummary` と**同じ判定**を使う (自動エリア 0 件 / 時間前入札は false)。
   * ここがズレると「ルールが出ていないのにボタンだけ『編集』」 になる。
   */
  hasSavedRule: boolean
  /** 全エリア一括編集モーダルを開く (AC-APF-039)。 */
  openBulkEdit: () => void
  /**
   * 編集モーダル。セル・一括のどちらから開いても同じ 1 つを使うので、
   * セクション内のどこかで **1 回だけ** 描画する。
   */
  editModal: React.ReactElement
}

export function useAutoBidSection({
  processDef,
  processState,
  autoBidRules,
}: {
  processDef: ProcessDef
  /** 未取得 (アコーディオン展開前) は undefined。その間は自動エリア 0 件として扱う。 */
  processState: ProcessState | undefined
  autoBidRules: readonly AutoBidRule[]
}): AutoBidSection {
  const { t } = useTranslation()
  const isIntraday = processDef.businessProcess === BusinessProcess.INTRADAY_BID
  // 入札制限の segment は業務処理で決まる (bid-limits §2)。
  const bidSegment = isIntraday ? BidSegment.INTRADAY : BidSegment.SPOT_NORMAL
  const { data: bidLimits } = useQuery(getBidLimitMatrix, { bidSegment })
  const fee = useAutoBidFee(
    isIntraday ? 'intraday' : 'spot',
    bidLimits?.feeAutoConsiderPolicy?.enabled ?? false,
  )
  const { mutateAsync: saveAsync } = useSaveAutoBidRule()
  const [editingArea, setEditingArea] = useState<AreaCode | 'bulk' | null>(null)

  const cellsByKey = processState?.cells
  const autoAreas = useMemo(
    () =>
      cellsByKey === undefined
        ? []
        : processDef.areas.filter((area) => {
            const key = makeNoSectionCellKey(processDef.businessProcess, area)
            return cellsByKey.get(key)?.mode === ApprovalMode.AUTO
          }),
    [processDef, cellsByKey],
  )

  const rulesByArea = useMemo(
    () => groupAutoBidRulesByArea(autoBidRules),
    [autoBidRules],
  )

  const cells = useMemo(() => bidLimits?.cells ?? [], [bidLimits])
  const marketOrderPolicyEntries = useMemo(
    () => bidLimits?.marketOrderPolicyEntries ?? [],
    [bidLimits],
  )

  const buyRangeFor = useCallback(
    (area: AreaCode) =>
      autoBidPriceRangeFromCells(cells, area, Side.BUY, bidSegment),
    [cells, bidSegment],
  )
  const sellRangeFor = useCallback(
    (area: AreaCode) =>
      autoBidPriceRangeFromCells(cells, area, Side.SELL, bidSegment),
    [cells, bidSegment],
  )
  const marketAllowedFor = useCallback(
    (area: AreaCode) =>
      autoBidMarketOrderAllowed(marketOrderPolicyEntries, area, bidSegment),
    [marketOrderPolicyEntries, bidSegment],
  )
  const buyQuantityLimitFor = useCallback(
    (area: AreaCode) =>
      autoBidQuantityLimitFromCells(cells, area, Side.BUY, bidSegment),
    [cells, bidSegment],
  )
  const sellQuantityLimitFor = useCallback(
    (area: AreaCode) =>
      autoBidQuantityLimitFromCells(cells, area, Side.SELL, bidSegment),
    [cells, bidSegment],
  )

  const handleSpotSave = useCallback(
    async (
      areaCodes: AreaCode[],
      buy: AutoBidSideLocal,
      sell: AutoBidSideLocal,
    ) => {
      try {
        await saveAsync({
          businessProcess: processDef.businessProcess,
          areaCodes,
          buyRule: autoBidSpotSideToProto(areaCodes[0], Side.BUY, buy),
          sellRule: autoBidSpotSideToProto(areaCodes[0], Side.SELL, sell),
        })
        setEditingArea(null)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        toaster.create({
          type: 'success',
          title: t('page:adminApprovalFlow.autoBid.save.success'),
        })
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        toaster.create({
          type: 'error',
          title: `${t('page:adminApprovalFlow.autoBid.save.error')}: ${detail}`,
        })
      }
    },
    [saveAsync, processDef.businessProcess, t],
  )

  const handleIntradaySave = useCallback(
    async (
      areaCodes: AreaCode[],
      buy: Parameters<typeof autoBidIntradaySideToProto>[2],
      sell: Parameters<typeof autoBidIntradaySideToProto>[2],
    ) => {
      try {
        await saveAsync({
          businessProcess: processDef.businessProcess,
          areaCodes,
          buyRule: autoBidIntradaySideToProto(areaCodes[0], Side.BUY, buy),
          sellRule: autoBidIntradaySideToProto(areaCodes[0], Side.SELL, sell),
        })
        setEditingArea(null)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        toaster.create({
          type: 'success',
          title: t('page:adminApprovalFlow.autoBid.save.success'),
        })
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        toaster.create({
          type: 'error',
          title: `${t('page:adminApprovalFlow.autoBid.save.error')}: ${detail}`,
        })
      }
    },
    [saveAsync, processDef.businessProcess, t],
  )

  const editingSingleArea = editingArea !== null && editingArea !== 'bulk'
  const editingBuy = editingSingleArea
    ? autoBidSideFromRule(rulesByArea.get(editingArea)?.buy, 'buy')
    : autoBidSideFromRule(undefined, 'buy')
  const editingSell = editingSingleArea
    ? autoBidSideFromRule(rulesByArea.get(editingArea)?.sell, 'sell')
    : autoBidSideFromRule(undefined, 'sell')

  const modalTitle = editingSingleArea
    ? t('page:adminApprovalFlow.autoBid.modal.titleArea', {
        area: AREA_CODE_LABELS[editingArea],
      })
    : t('page:adminApprovalFlow.autoBid.modal.titleBulk')

  /**
   * 時間前ルール片側の要約行 (AC-APF-047)。 書式は時間前の text 形で、 ⚠ は
   * 価格を直接入力する欄から求めた実効値だけを対象にする。 セル内サマリと一括要約が
   * 同じ行を使う (AC-APF-060)。
   */
  const intradaySideRow = useCallback(
    (
      rule: AutoBidRule | undefined,
      area: AreaCode,
      sideKind: 'buy' | 'sell',
    ) => {
      const side = autoBidIntradaySideFromRule(rule, sideKind)
      return {
        text: autoBidIntradaySideSummaryText(t, side, sideKind, fee),
        effective: computeAutoBidIntradayEffective(
          side,
          sideKind,
          sideKind === 'buy' ? buyRangeFor(area) : sellRangeFor(area),
          fee,
        ),
      }
    },
    [t, buyRangeFor, sellRangeFor, fee],
  )

  const renderCellSummary = useCallback(
    (area: AreaCode): React.ReactElement | null => {
      if (!autoAreas.includes(area)) return null
      const rule = rulesByArea.get(area)
      if (isIntraday) {
        return (
          <AutoBidCellSummary
            buy={intradaySideRow(rule?.buy, area, 'buy')}
            onEdit={() => setEditingArea(area)}
            sell={intradaySideRow(rule?.sell, area, 'sell')}
            unsaved={!isAutoBidIntradayRuleSaved(rulesByArea, area)}
          />
        )
      }
      const buy = autoBidSideFromRule(rule?.buy, 'buy')
      const sell = autoBidSideFromRule(rule?.sell, 'sell')
      const buyRange = buyRangeFor(area)
      const sellRange = sellRangeFor(area)
      const marketAllowed = marketAllowedFor(area)
      return (
        <AutoBidCellSummary
          buy={{
            side: buy,
            effective: computeAutoBidEffective(
              buy,
              'buy',
              buyRange,
              marketAllowed,
              fee,
            ),
          }}
          onEdit={() => setEditingArea(area)}
          sell={{
            side: sell,
            effective: computeAutoBidEffective(
              sell,
              'sell',
              sellRange,
              marketAllowed,
              fee,
            ),
          }}
          // AC-01/AC-056b: 買い・売り両方が明示保存されていない間は
          // デフォルト値サマリを見せず「未設定（ルール編集が必要）」を表示する。
          unsaved={isAutoBidRuleUnsaved(rulesByArea, area)}
        />
      )
    },
    [
      autoAreas,
      rulesByArea,
      isIntraday,
      intradaySideRow,
      buyRangeFor,
      sellRangeFor,
      marketAllowedFor,
      fee,
    ],
  )

  /**
   * 一括 = `自動` の折りたたみ要約 (AC-APF-060)。
   *
   * マトリクスが非表示になるとセル内サマリ (AC-APF-037) が見えず、 下のパネルの
   * 説明表は各項目の **意味**を示す凡例であって設定値ではない。 そのため
   * 「全 N エリアに一括設定しています」 だけでは、 いま何が設定されているかを
   * モーダルを開かずに確認する手段が無かった (#7434 PdM 報告)。
   *
   * 一括 = `自動` は全エリアへ同じ値を適用する運用 (`openBulkEdit` の保存先が
   * `autoAreas` 全件) なので、 **保存済みの先頭エリアを代表**として 1 組だけ出す。
   * エリアごとに違う値を並べると 1 行に収まらず要約の意味が無くなる (design 同判断)。
   */
  /**
   * 保存済みルールを持つ代表エリア (= `bulkSummary` が実値を出す条件)。
   * 自動エリア 0 件は対象外。 判定は `bulkSummary` 側と同じ条件を使う。
   *
   * 時間前入札は `process_config` が intraday であることまで要求する
   * ([[isAutoBidIntradayRuleSaved]])。 buy/sell の有無だけを見る
   * [[isAutoBidRuleUnsaved]] では、 spot のルールが混ざった時に
   * 「保存済みなのに既定値」 という表示になり得るため (鉄則2)。
   */
  const hasSavedRule = useMemo(
    (): boolean =>
      autoAreas.length > 0 &&
      autoAreas.some((area) =>
        isIntraday
          ? isAutoBidIntradayRuleSaved(rulesByArea, area)
          : !isAutoBidRuleUnsaved(rulesByArea, area),
      ),
    [autoAreas, isIntraday, rulesByArea],
  )

  const bulkSummary = useMemo((): React.ReactElement | null => {
    if (autoAreas.length === 0) return null
    /* 時間前入札は intraday 専用の書式で出す (AC-APF-047)。 spot 用の
       `autoBidSideFromRule` は spot 以外を既定値へ落とし、 未保存を設定済みに
       見せるため通さない (鉄則2、 #7874)。 */
    if (isIntraday) {
      const repArea = autoAreas.find((area) =>
        isAutoBidIntradayRuleSaved(rulesByArea, area),
      )
      if (repArea === undefined) return <AutoBidBulkSummary rows={undefined} />
      const rule = rulesByArea.get(repArea)
      return (
        <AutoBidBulkSummary
          rows={{
            buy: intradaySideRow(rule?.buy, repArea, 'buy'),
            sell: intradaySideRow(rule?.sell, repArea, 'sell'),
          }}
        />
      )
    }
    const repArea = autoAreas.find(
      (area) => !isAutoBidRuleUnsaved(rulesByArea, area),
    )
    if (repArea === undefined) {
      // 買い・売りを両方持つエリアが 1 つも無い = 一度も明示保存されていない。
      return <AutoBidBulkSummary rows={undefined} />
    }
    const rule = rulesByArea.get(repArea)
    const buy = autoBidSideFromRule(rule?.buy, 'buy')
    const sell = autoBidSideFromRule(rule?.sell, 'sell')
    const marketAllowed = marketAllowedFor(repArea)
    return (
      <AutoBidBulkSummary
        rows={{
          buy: {
            side: buy,
            effective: computeAutoBidEffective(
              buy,
              'buy',
              buyRangeFor(repArea),
              marketAllowed,
              fee,
            ),
          },
          sell: {
            side: sell,
            effective: computeAutoBidEffective(
              sell,
              'sell',
              sellRangeFor(repArea),
              marketAllowed,
              fee,
            ),
          },
        }}
      />
    )
  }, [
    autoAreas,
    rulesByArea,
    buyRangeFor,
    sellRangeFor,
    marketAllowedFor,
    isIntraday,
    intradaySideRow,
    fee,
  ])

  const openBulkEdit = useCallback(() => {
    setEditingArea('bulk')
  }, [])

  const editModal = isIntraday ? (
    // 時間前に成行は無いので成行可否は渡さない。 一括モーダルは上下限なし
    // (スポットと同じ。 AC-APF-043 の対象はエリア別モーダル)。
    <AutoBidIntradayEditModal
      buyPriceRange={
        editingSingleArea
          ? buyRangeFor(editingArea)
          : { min: undefined, max: undefined }
      }
      buyQuantityLimit={
        editingSingleArea ? buyQuantityLimitFor(editingArea) : undefined
      }
      fee={fee}
      initialBuy={autoBidIntradaySideFromRule(
        editingSingleArea ? rulesByArea.get(editingArea)?.buy : undefined,
        'buy',
      )}
      initialSell={autoBidIntradaySideFromRule(
        editingSingleArea ? rulesByArea.get(editingArea)?.sell : undefined,
        'sell',
      )}
      isOpen={editingArea !== null}
      onClose={() => setEditingArea(null)}
      onSave={(buy, sell) => {
        const areaCodes = editingSingleArea ? [editingArea] : autoAreas
        void handleIntradaySave(areaCodes, buy, sell)
      }}
      sellPriceRange={
        editingSingleArea
          ? sellRangeFor(editingArea)
          : { min: undefined, max: undefined }
      }
      sellQuantityLimit={
        editingSingleArea ? sellQuantityLimitFor(editingArea) : undefined
      }
      subtitle={
        editingSingleArea
          ? undefined
          : t('page:adminApprovalFlow.autoBid.modal.subtitleBulk')
      }
      title={modalTitle}
    />
  ) : (
    <AutoBidEditModal
      buyMarketOrderAllowed={
        editingSingleArea ? marketAllowedFor(editingArea) : true
      }
      buyPriceRange={
        editingSingleArea
          ? buyRangeFor(editingArea)
          : { min: undefined, max: undefined }
      }
      buyQuantityLimit={
        editingSingleArea ? buyQuantityLimitFor(editingArea) : undefined
      }
      fee={fee}
      initialBuy={editingBuy}
      initialSell={editingSell}
      isOpen={editingArea !== null}
      onClose={() => setEditingArea(null)}
      onSave={(buy, sell) => {
        const areaCodes = editingSingleArea ? [editingArea] : autoAreas
        void handleSpotSave(areaCodes, buy, sell)
      }}
      sellMarketOrderAllowed={
        editingSingleArea ? marketAllowedFor(editingArea) : true
      }
      sellPriceRange={
        editingSingleArea
          ? sellRangeFor(editingArea)
          : { min: undefined, max: undefined }
      }
      sellQuantityLimit={
        editingSingleArea ? sellQuantityLimitFor(editingArea) : undefined
      }
      subtitle={
        editingSingleArea
          ? undefined
          : t('page:adminApprovalFlow.autoBid.modal.subtitleBulk')
      }
      title={modalTitle}
    />
  )

  return {
    autoAreas,
    renderCellSummary,
    bulkSummary,
    hasSavedRule,
    openBulkEdit,
    editModal,
  }
}
